'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import Script from 'next/script';
import {
  generateWebsiteJsonLd,
  generateOrganizationJsonLd,
  generateBreadcrumbJsonLd,
  generateRealEstateListingJsonLd,
  generateLocalBusinessJsonLd,
} from '../../../lib/json-ld';

export default function JsonLd({ type = 'all', data, url }) {
  const pathname = usePathname();
  const [jsonLdData, setJsonLdData] = useState([]);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    const baseUrl = 'https://x-space.ae';
    const fullUrl = url || `${baseUrl}${pathname}`;
    setCurrentUrl(fullUrl);

    const jsonLdArray = [];

    if (type === 'website' || type === 'all') {
      jsonLdArray.push(generateWebsiteJsonLd('X-SPACE Real Estate Dubai', baseUrl));
    }

    if (type === 'organization' || type === 'all') {
      jsonLdArray.push(
        generateOrganizationJsonLd(
          'X-SPACE Real Estate',
          baseUrl,
          'https://images.x-space.ae/assets/images/Logo-B.png',
          [
            'https://www.facebook.com/xspacerealestate',
            'https://www.instagram.com/xspace_realestate',
            'https://www.linkedin.com/feed/update/urn:li:activity:7176518870264938498',
            'https://www.tiktok.com/@xspace_realestate?_t=ZS-8uVY38cKpRy&_r=1',
          ],
        )
      );
    }

    if (type === 'breadcrumb' || type === 'all') {
      const pathSegments = pathname
        .split('/')
        .filter(Boolean)
        .map((segment, index, array) => {
          const url = `${baseUrl}/${array.slice(0, index + 1).join('/')}`;
          return {
            name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
            url,
          };
        });

      const breadcrumbItems = [{ name: 'Home', url: baseUrl }, ...pathSegments];

      if (breadcrumbItems.length > 1) {
        jsonLdArray.push(generateBreadcrumbJsonLd(breadcrumbItems));
      }
    }

    if (type === 'property' && data) {
      jsonLdArray.push(
        generateRealEstateListingJsonLd({
          name: data.title || 'Property Listing',
          description: data.description || 'Property listing in Dubai',
          image: data.image || 'https://images.x-space.ae/assets/images/Logo-B.png',
          url: fullUrl,
          price: data.price?.toString(),
          currency: 'AED',
          address: data.location,
          city: 'Dubai',
          country: 'AE',
          bedrooms: data.beds,
          area: data.area,
        })
      );
    }

    if (type === 'localBusiness' || (type === 'all' && pathname === '/about')) {
      jsonLdArray.push(generateLocalBusinessJsonLd());
    }

    setJsonLdData(jsonLdArray);
  }, [pathname, type, data, url]);

  if (!jsonLdData.length) return null;

  const scriptContent = {
    __html: JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': jsonLdData,
    }),
  };

  return (
    <Script
      id={`json-ld-${type}`}
      type='application/ld+json'
      dangerouslySetInnerHTML={scriptContent}
      strategy='afterInteractive'
    />
  );
}
